import { Match, SeasonStats, shouldApplyParticipationBonus, FIRST_SEASON_ID } from '../types';
import { storage } from './storage';

export const calculatePlayerStats = async (playerId: string, seasonId?: string): Promise<SeasonStats> => {
  const matches = await storage.getMatches();
  const players = await storage.getPlayers();
  const player = players.find(p => p.id === playerId);
  if (!player) {
    return {
      matches: 0,
      wins: 0,
      draws: 0,
      losses: 0,
      goals: 0,
      assists: 0,
      ownGoals: 0,
      points: 0,
      blackCards: 0,
      mvdpCards: 0,
    };
  }

  const currentSeasonId = seasonId || await storage.getCurrentSeason();
  const seasonMatches = matches.filter(m => m.seasonId === currentSeasonId);

  let stats: SeasonStats = {
    matches: 0,
    wins: 0,
    draws: 0,
    losses: 0,
    goals: 0,
    assists: 0,
    ownGoals: 0,
    points: 0,
    blackCards: 0,
    mvdpCards: 0,
  };

  seasonMatches.forEach(match => {
    // Match verilerinin geçerli olduğundan emin ol
    if (!match || !match.blackTeam || !match.whiteTeam) {
      return;
    }

    const isInBlackTeam = match.blackTeam.includes(playerId);
    const isInWhiteTeam = match.whiteTeam.includes(playerId);
    const played = isInBlackTeam || isInWhiteTeam;

    if (played) {
      stats.matches += 1;

      // Maç sonucu
      const blackWon = match.blackScore > match.whiteScore;
      const whiteWon = match.whiteScore > match.blackScore;
      const draw = match.blackScore === match.whiteScore;

      if (isInBlackTeam) {
        if (blackWon) stats.wins += 1;
        else if (draw) stats.draws += 1;
        else stats.losses += 1;
      } else {
        if (whiteWon) stats.wins += 1;
        else if (draw) stats.draws += 1;
        else stats.losses += 1;
      }

      // Gol ve asistler
      if (match.events && Array.isArray(match.events)) {
        match.events.forEach(event => {
          if (event.scorerId === playerId && !event.ownGoal) {
            stats.goals += 1;
          }
          if (event.scorerId === playerId && event.ownGoal) {
            stats.ownGoals += 1;
          }
          if (event.assistId === playerId) {
            stats.assists += 1;
          }
        });
      }

      // Kartlar
      if (match.blackCard === playerId) stats.blackCards += 1;
      if (match.mvdp === playerId) stats.mvdpCards += 1;
    }
  });

  // Puan: 1. sezon = Galibiyet - Mağlubiyet; 2. sezon+ = Galibiyet 3, Beraberlik 1, Mağlubiyet 0
  if (shouldApplyParticipationBonus(currentSeasonId)) {
    stats.points = stats.wins * 3 + stats.draws * 1;
  } else {
    stats.points = stats.wins - stats.losses;
  }

  return stats;
};

export const getAllTimeStats = async (playerId: string): Promise<SeasonStats> => {
  const matches = await storage.getMatches();
  const players = await storage.getPlayers();
  const player = players.find(p => p.id === playerId);
  if (!player) {
    return {
      matches: 0,
      wins: 0,
      draws: 0,
      losses: 0,
      goals: 0,
      assists: 0,
      ownGoals: 0,
      points: 0,
      blackCards: 0,
      mvdpCards: 0,
    };
  }

  let stats: SeasonStats = {
    matches: 0,
    wins: 0,
    draws: 0,
    losses: 0,
    goals: 0,
    assists: 0,
    ownGoals: 0,
    points: 0,
    blackCards: 0,
    mvdpCards: 0,
  };

  let winsS1 = 0, lossesS1 = 0, winsS2Plus = 0, drawsS2Plus = 0;

  matches.forEach(match => {
    // Match verilerinin geçerli olduğundan emin ol
    if (!match || !match.blackTeam || !match.whiteTeam) {
      return;
    }

    const isInBlackTeam = match.blackTeam.includes(playerId);
    const isInWhiteTeam = match.whiteTeam.includes(playerId);
    const played = isInBlackTeam || isInWhiteTeam;

    if (played) {
      stats.matches += 1;

      const blackWon = match.blackScore > match.whiteScore;
      const whiteWon = match.whiteScore > match.blackScore;
      const draw = match.blackScore === match.whiteScore;

      const isWin = (isInBlackTeam && blackWon) || (isInWhiteTeam && whiteWon);
      const isLoss = (isInBlackTeam && !blackWon && !draw) || (isInWhiteTeam && !whiteWon && !draw);

      if (match.seasonId === FIRST_SEASON_ID) {
        if (isWin) winsS1 += 1;
        else if (isLoss) lossesS1 += 1;
      } else {
        if (isWin) winsS2Plus += 1;
        else if (draw) drawsS2Plus += 1;
      }

      if (isInBlackTeam) {
        if (blackWon) stats.wins += 1;
        else if (draw) stats.draws += 1;
        else stats.losses += 1;
      } else {
        if (whiteWon) stats.wins += 1;
        else if (draw) stats.draws += 1;
        else stats.losses += 1;
      }

      // Gol ve asistler
      if (match.events && Array.isArray(match.events)) {
        match.events.forEach(event => {
          if (event.scorerId === playerId && !event.ownGoal) {
            stats.goals += 1;
          }
          if (event.scorerId === playerId && event.ownGoal) {
            stats.ownGoals += 1;
          }
          if (event.assistId === playerId) {
            stats.assists += 1;
          }
        });
      }

      if (match.blackCard === playerId) stats.blackCards += 1;
      if (match.mvdp === playerId) stats.mvdpCards += 1;
    }
  });

  // Puan: 1. sezon = G-M; 2. sezon+ = Galibiyet 3, Beraberlik 1, Mağlubiyet 0
  stats.points = (winsS1 - lossesS1) + (winsS2Plus * 3) + (drawsS2Plus * 1);

  return stats;
};

// Top players cache
let topPlayersCache: { data: any; timestamp: number; seasonId: string } | null = null;

// Mobil cihaz tespiti
const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
};

// Mobilde daha uzun cache (API limitlerini korumak için)
const TOP_PLAYERS_CACHE_TTL = isMobileDevice() ? 30 * 60 * 1000 : 10 * 60 * 1000; // Mobilde 30 dakika, PC'de 10 dakika

export const getTopPlayers = async () => {
  const players = await storage.getPlayers();
  const currentSeasonId = await storage.getCurrentSeason();
  
  // Cache kontrolü
  if (topPlayersCache && 
      topPlayersCache.seasonId === currentSeasonId &&
      Date.now() - topPlayersCache.timestamp < TOP_PLAYERS_CACHE_TTL) {
    return topPlayersCache.data;
  }

  // Tüm maçları bir kez çek (her oyuncu için tekrar çekmeyelim)
  const matches = await storage.getMatches();
  const seasonMatches = matches.filter(m => m.seasonId === currentSeasonId);
  
  // İstatistikleri hesapla (cache'lenmiş verilerle)
  const playersWithStats = players.map(player => {
    let stats = {
      matches: 0,
      wins: 0,
      draws: 0,
      losses: 0,
      goals: 0,
      assists: 0,
      ownGoals: 0,
      points: 0,
      blackCards: 0,
      mvdpCards: 0,
    };

    seasonMatches.forEach(match => {
      if (!match || !match.blackTeam || !match.whiteTeam) return;

      const isInBlackTeam = match.blackTeam.includes(player.id);
      const isInWhiteTeam = match.whiteTeam.includes(player.id);
      const played = isInBlackTeam || isInWhiteTeam;

      if (played) {
        stats.matches += 1;
        const blackWon = match.blackScore > match.whiteScore;
        const whiteWon = match.whiteScore > match.blackScore;
        const draw = match.blackScore === match.whiteScore;

        if (isInBlackTeam) {
          if (blackWon) stats.wins += 1;
          else if (draw) stats.draws += 1;
          else stats.losses += 1;
        } else {
          if (whiteWon) stats.wins += 1;
          else if (draw) stats.draws += 1;
          else stats.losses += 1;
        }

        if (match.events && Array.isArray(match.events)) {
          match.events.forEach(event => {
            if (event.scorerId === player.id && !event.ownGoal) stats.goals += 1;
            if (event.scorerId === player.id && event.ownGoal) stats.ownGoals += 1;
            if (event.assistId === player.id) stats.assists += 1;
          });
        }

        if (match.blackCard === player.id) stats.blackCards += 1;
        if (match.mvdp === player.id) stats.mvdpCards += 1;
      }
    });

    // Puan: 1. sezon = G-M; 2. sezon+ = Galibiyet 3, Beraberlik 1, Mağlubiyet 0
    if (shouldApplyParticipationBonus(currentSeasonId)) {
      stats.points = stats.wins * 3 + stats.draws * 1;
    } else {
      stats.points = stats.wins - stats.losses;
    }
    return { player, stats };
  });

  // Lider: En çok puan → eşitlikte en çok gol → eşitlikte en çok kart (siyah + mvdp)
  const leader = [...playersWithStats].sort((a, b) => {
    if (b.stats.points !== a.stats.points) return b.stats.points - a.stats.points;
    if (b.stats.goals !== a.stats.goals) return b.stats.goals - a.stats.goals;
    const aCards = a.stats.blackCards + a.stats.mvdpCards;
    const bCards = b.stats.blackCards + b.stats.mvdpCards;
    return bCards - aCards;
  })[0];

  // Gol Katkısı: En çok gol+asist → eşitlikte en çok galibiyet
  const topGoalContribution = [...playersWithStats].sort((a, b) => {
    const aContribution = a.stats.goals + a.stats.assists;
    const bContribution = b.stats.goals + b.stats.assists;
    if (bContribution !== aContribution) return bContribution - aContribution;
    return b.stats.wins - a.stats.wins;
  })[0];

  // Gol Kralı: En çok gol → eşitlikte en çok maç kazanan
  const topScorer = [...playersWithStats].sort((a, b) => {
    if (b.stats.goals !== a.stats.goals) return b.stats.goals - a.stats.goals;
    return b.stats.wins - a.stats.wins;
  })[0];

  // Asist Kralı: En çok asist → eşitlikte en çok maç kazanan
  const topAssister = [...playersWithStats].sort((a, b) => {
    if (b.stats.assists !== a.stats.assists) return b.stats.assists - a.stats.assists;
    return b.stats.wins - a.stats.wins;
  })[0];

  // En Sadık: En çok maç → eşitlikte en çok maç kazanan
  const mostLoyal = [...playersWithStats].sort((a, b) => {
    if (b.stats.matches !== a.stats.matches) return b.stats.matches - a.stats.matches;
    return b.stats.wins - a.stats.wins;
  })[0];

  // Konserci: En az maç → eşitlikte en çok mağlubiyet
  const leastLoyal = [...playersWithStats].sort((a, b) => {
    if (a.stats.matches !== b.stats.matches) return a.stats.matches - b.stats.matches;
    return b.stats.losses - a.stats.losses;
  })[0];

  const result = {
    leader: leader ? { player: leader.player, value: leader.stats.points } : null,
    topGoalContribution: topGoalContribution ? { player: topGoalContribution.player, value: topGoalContribution.stats.goals + topGoalContribution.stats.assists } : null,
    topScorer: topScorer ? { player: topScorer.player, value: topScorer.stats.goals } : null,
    topAssister: topAssister ? { player: topAssister.player, value: topAssister.stats.assists } : null,
    mostLoyal: mostLoyal ? { player: mostLoyal.player, value: mostLoyal.stats.matches } : null,
    leastLoyal: leastLoyal ? { player: leastLoyal.player, value: leastLoyal.stats.matches } : null,
  };
  
  // Cache'e kaydet
  topPlayersCache = {
    data: result,
    timestamp: Date.now(),
    seasonId: currentSeasonId,
  };
  
  return result;
};

export const getLastMatch = async (): Promise<Match | null> => {
  const matches = await storage.getMatches();
  if (matches.length === 0) return null;
  return [...matches].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
};

export const calculateDetailedStats = (stats: SeasonStats) => {
  const matches = stats.matches || 0;
  
  // Oranlar
  const winRate = matches > 0 ? ((stats.wins / matches) * 100).toFixed(1) : '0.0';
  const drawRate = matches > 0 ? ((stats.draws / matches) * 100).toFixed(1) : '0.0';
  const lossRate = matches > 0 ? ((stats.losses / matches) * 100).toFixed(1) : '0.0';
  
  // Maç başına istatistikler (yüzde formatında - maç başına değer direkt yüzde olarak gösterilir)
  // Örn: maç başına 1.5 gol = %1.5
  const goalsPerMatch = matches > 0 ? (stats.goals / matches).toFixed(1) : '0.0';
  const assistsPerMatch = matches > 0 ? (stats.assists / matches).toFixed(1) : '0.0';
  const pointsPerMatch = matches > 0 ? (stats.points / matches).toFixed(1) : '0.0';
  
  // Toplam katkılar
  const totalContribution = stats.goals + stats.assists;
  const contributionPerMatch = matches > 0 ? (totalContribution / matches).toFixed(2) : '0.00';
  
  // Gol katkısı yüzdesi (maç başına gol + asist oranı, aynı formatta maç başına gol/asist gibi)
  const goalContributionPercentage = matches > 0 ? (totalContribution / matches).toFixed(1) : '0.0';
  
  // Kart istatistikleri
  const totalCards = stats.blackCards + stats.mvdpCards;
  const cardsPerMatch = matches > 0 ? (totalCards / matches).toFixed(2) : '0.00';
  
  // Puan verimliliği
  const pointEfficiency = matches > 0 ? ((stats.points / (matches * 3)) * 100).toFixed(1) : '0.0';

  return {
    winRate: `${winRate}%`,
    drawRate: `${drawRate}%`,
    lossRate: `${lossRate}%`,
    goalsPerMatch: `${goalsPerMatch}%`,
    assistsPerMatch: `${assistsPerMatch}%`,
    pointsPerMatch: `${pointsPerMatch}%`,
    totalContribution,
    contributionPerMatch,
    goalContributionPercentage: `${goalContributionPercentage}%`,
    totalCards,
    cardsPerMatch,
    pointEfficiency: `${pointEfficiency}%`,
  };
};
